var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useIsActiveScope } from 'ziphy-web-shared/basic/context/ScopeContext';
import { getUuid } from 'ziphy-web-shared/basic/helpers';
import { Button } from 'ziphy-web-shared/basic/lib/buttons';
import { Input } from 'ziphy-web-shared/basic/lib/forms/fields';
import { DropdownInput } from 'ziphy-web-shared/basic/lib/functional';
import { WithLoaderWrapper } from 'ziphy-web-shared/basic/lib/loaders';
import { ContainerScrollable, TagWithRemove } from 'ziphy-web-shared/basic/lib/simple';
import { $loader, globalLoaderHandlers } from 'ziphy-web-shared/basic/utils';
import { DropdownContext, DropdownDate, useDropdownFocusedIndex, } from '../';
import { isGroupedDropdownItems as isGrouped } from '../Dropdown.helpers';
import styles from './DropdownContent.module.scss';
import { DropdownContentItem } from './DropdownContentItem';
export const DropdownContent = observer(({ items, searchField, classNameItem, isLoading, isMulti, isManualCloseControl, onOpen, onDone, 
//
selectedItems = null, onRemoveItem, onRemoveAll, 
//
otherField, title = '', mountEventName, }) => {
    const { t } = useTranslation();
    const contentId = useMemo(() => getUuid(), []);
    const context = useContext(DropdownContext);
    const { onClose } = context;
    const isActive = useIsActiveScope();
    const otherRef = useRef(null);
    const [loadingItemId, setLoadingItemId] = useState(-1);
    const focused = useDropdownFocusedIndex(items, otherRef, isActive);
    function handleClose() {
        if (isManualCloseControl || isMulti)
            return;
        onClose();
    }
    const handleClick = useCallback((item, event) => {
        if (typeof item.onClick === 'function') {
            const promise = item.onClick(event);
            if (promise instanceof Promise) {
                setLoadingItemId(item.id);
                promise.finally(() => {
                    setLoadingItemId(null);
                    $loader.simpleTrigger(globalLoaderHandlers.DROPDOWN_CLICK, item);
                    handleClose();
                });
            }
            else {
                handleClose();
            }
        }
        else {
            handleClose();
        }
    }, []);
    useEffect(() => {
        if (mountEventName) {
            window.dispatchEvent(new CustomEvent(mountEventName, { detail: { mounted: true } }));
            return () => {
                window.dispatchEvent(new CustomEvent(mountEventName, { detail: { mounted: false } }));
            };
        }
    }, []);
    useEffect(() => {
        if (onOpen) {
            $loader.registerHandler(contentId, () => __awaiter(void 0, void 0, void 0, function* () { return yield onOpen(onClose); }))();
        }
    }, [onOpen]);
    useEffect(() => {
        const onKeydown = (e) => {
            var _a;
            if (e.key === 'Enter') {
                e.preventDefault();
                const focusedItem = isGrouped(items)
                    ? (_a = items[focused.group]) === null || _a === void 0 ? void 0 : _a.items[focused.item]
                    : items[focused.item];
                if (focusedItem) {
                    handleClick(focusedItem);
                }
            }
        };
        if (isActive) {
            window.addEventListener('keydown', onKeydown);
        }
        return () => window.removeEventListener('keydown', onKeydown);
    }, [focused.item, isActive]);
    useEffect(() => {
        var _a, _b;
        const dropdownItems = isGrouped(items) ? (_a = items[focused.group]) === null || _a === void 0 ? void 0 : _a.items : items;
        if (focused.item >= 0 && dropdownItems) {
            const otherElement = (_b = otherRef.current) === null || _b === void 0 ? void 0 : _b.originalFieldRef.current;
            const item = dropdownItems[focused.item];
            if (focused.isOtherFocused) {
                otherElement === null || otherElement === void 0 ? void 0 : otherElement.focus();
                otherElement === null || otherElement === void 0 ? void 0 : otherElement.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
            }
            else if (item) {
                otherElement === null || otherElement === void 0 ? void 0 : otherElement.blur();
                const element = document.getElementById(item.id);
                element === null || element === void 0 ? void 0 : element.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
            }
        }
    }, [focused.item]);
    const isGroupedList = isGrouped(items);
    const isEmptyList = isGroupedList
        ? items.length === 0 || (items.length === 1 && items[0].items.length === 0)
        : items.length === 0;
    const hasHeader = Boolean(searchField);
    const hasFooter = Boolean(isMulti) && !isLoading;
    return (_jsxs(_Fragment, { children: [searchField && (_jsx(Input, { field: searchField, mode: ['sm'], leftElement: 'search', className: styles.searchField, autoFocus: true, clear: true })), _jsx(ContainerScrollable, Object.assign({ wrapperClassName: classNames(styles.scrollableArea, hasHeader && styles.resetTop, hasFooter && styles.resetBottom) }, { children: _jsxs(WithLoaderWrapper, Object.assign({ isLoading: isLoading, mode: 'update', loader: 'bubble', withWrapper: false }, { children: [(selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.length) > 0 && !isLoading && (_jsxs("div", Object.assign({ className: styles.badges }, { children: [onRemoveAll && (_jsx("div", Object.assign({ className: styles.clearAll, onClick: onRemoveAll }, { children: t('btn.clear_all') }))), selectedItems.map((item) => (_jsx(TagWithRemove, Object.assign({ onRemove: () => onRemoveItem(item) }, { children: item }), item)))] }))), title && !isLoading ? _jsx("div", Object.assign({ className: styles.title }, { children: title })) : null, isEmptyList && !isLoading ? (_jsx("div", Object.assign({ className: classNames(styles.notFound, (hasHeader || hasFooter) && 'mb-10') }, { children: (searchField === null || searchField === void 0 ? void 0 : searchField.value.length) > 0
                                ? t('label.search.no_results', { searchString: searchField.value })
                                : t('label.start_searching_to_select') }))) : isGroupedList ? (items.map((item, groupIndex) => (_jsxs(Fragment, { children: [item.label && _jsx("div", Object.assign({ className: styles.itemTitle }, { children: item.label })), item.items.map((item, index) => (_jsx(DropdownContentItem, { item: item, onClick: (e) => handleClick(item, e), className: classNameItem, isSelected: selectedItems === null ? undefined : selectedItems.includes(item.id), isFocused: focused.group === groupIndex && focused.item === index, isLoading: loadingItemId === item.id, isMulti: isMulti }, item.id)))] }, item.id || groupIndex)))) : (items.map((item, index) => (_jsx(DropdownContentItem, { item: item, onClick: (e) => handleClick(item, e), className: classNameItem, isSelected: selectedItems === null ? undefined : selectedItems.includes(item.id), isFocused: focused.item === index, isLoading: loadingItemId === item.id, isMulti: isMulti }, item.id)))), (otherField === null || otherField === void 0 ? void 0 : otherField.element) === 'date' && (_jsx(DropdownDate, Object.assign({ ref: otherRef }, otherField))), (otherField === null || otherField === void 0 ? void 0 : otherField.element) === 'input' && (_jsx(DropdownInput, Object.assign({ ref: otherRef }, otherField)))] })) })), hasFooter && (_jsx("div", Object.assign({ className: styles.footer }, { children: _jsx(Button, Object.assign({ mode: ['secondary', 'block', 'sm'], onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                        if (typeof onDone === 'function') {
                            $loader.registerHandler(contentId, () => __awaiter(void 0, void 0, void 0, function* () { return yield onDone(onClose); }))();
                        }
                        else {
                            onClose();
                        }
                    }), isLoading: $loader.isRunHandler(contentId) }, { children: t('btn.done') })) })))] }));
});
